import React, { useState } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { 
  Container, 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Paper, 
  Alert,
  Link,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar
} from '@mui/material';
import { useAuth } from './useAuth';
import LoadingContainer from '../../components/common/LoadingContainer';
import InlineLoader from '../../components/common/InlineLoader';

const LoginPage: React.FC = () => {
  const { login, user, loading, resetPassword } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Forgot password states
  const [openForgotDialog, setOpenForgotDialog] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);
  const [isResetting, setIsResetting] = useState(false);
  
  // Get redirect path from location state or default to dashboard
  const from = (location.state as any)?.from?.pathname || '/dashboard';
  
  // If user is already logged in, redirect to dashboard
  if (user && !loading) {
    return <Navigate to={from} replace />;
  }
  
  const handleOpenForgotDialog = () => {
    setForgotEmail(email); // Pre-fill with login email if available
    setResetError(null);
    setOpenForgotDialog(true);
  };

  const handleCloseForgotDialog = () => {
    setOpenForgotDialog(false);
  };

  const handleResetPassword = async () => {
    if (!forgotEmail || !forgotEmail.trim()) {
      setResetError('Please enter your email address');
      return;
    }

    setIsResetting(true);
    setResetError(null);
    
    try {
      await resetPassword(forgotEmail);
      setResetSuccess(true);
      setOpenForgotDialog(false);
    } catch (error: any) {
      // Handle different Firebase Auth errors
      if (error.code === 'auth/user-not-found') {
        setResetError('No account found with this email address');
      } else if (error.code === 'auth/invalid-email') {
        setResetError('Invalid email address');
      } else {
        setResetError('An error occurred. Please try again');
      }
      console.error('Password reset error:', error);
    } finally {
      setIsResetting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    
    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (error: any) {
      // Handle different Firebase Auth errors
      if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
        setError('Invalid email or password');
      } else if (error.code === 'auth/too-many-requests') {
        setError('Too many failed login attempts. Please try again later');
      } else {
        setError('An error occurred. Please try again');
      }
      console.error('Login error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper 
          elevation={3} 
          sx={{ 
            p: 4, 
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            borderRadius: 2,
          }}
        >
          <Box
            component="img"
            src="/logo192.png"
            alt="UFIT Logo"
            sx={{
              width: 120,
              height: 120,
              mb: 2,
              objectFit: 'contain'
            }}
          />
          
          <Typography component="h1" variant="h4" gutterBottom>
            Client Management System
          </Typography>
          
          <Typography component="h2" variant="h5" gutterBottom>
            Sign In
          </Typography>
          
          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1, width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isSubmitting}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isSubmitting}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2, py: 1.5 }}
              disabled={isSubmitting}
            >
              {isSubmitting ? <InlineLoader size={24} /> : 'Sign In'}
            </Button>
            
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Link href="#" variant="body2" onClick={(e) => {
                e.preventDefault();
                handleOpenForgotDialog();
              }}>
                Forgot password?
              </Link>
            </Box>
          </Box>
        </Paper>
      </Box>

      {/* Forgot Password Dialog */}
      <Dialog open={openForgotDialog} onClose={handleCloseForgotDialog}>
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Enter your email address and we'll send you a link to reset your password.
          </DialogContentText>
          {resetError && (
            <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
              {resetError}
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            id="reset-email"
            label="Email Address"
            type="email"
            fullWidth
            variant="outlined"
            value={forgotEmail}
            onChange={(e) => setForgotEmail(e.target.value)}
            disabled={isResetting}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseForgotDialog} disabled={isResetting}>
            Cancel
          </Button>
          <Button 
            onClick={handleResetPassword} 
            variant="contained" 
            color="primary"
            disabled={isResetting}
          >
            {isResetting ? <InlineLoader size={24} /> : 'Reset Password'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Password Reset Success Snackbar */}
      <Snackbar 
        open={resetSuccess} 
        autoHideDuration={6000} 
        onClose={() => setResetSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setResetSuccess(false)} severity="success" sx={{ width: '100%' }}>
          Password reset email sent! Check your inbox.
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default LoginPage;
